import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../lib/i18n";

export default function Blog() {
  const { t } = useLang();
  const posts = t.blog.posts;
  const [featured, ...rest] = posts;

  return (
    <div data-testid="blog-page">
      <section className="container-x pt-12 pb-12 md:pt-20 md:pb-16">
        <span className="label-eyebrow">{t.blog.eyebrow}</span>
        <h1 className="mt-5 font-serif text-5xl md:text-7xl leading-[1.02] tracking-tight text-ink max-w-4xl">
          {t.blog.title}
        </h1>
        <p className="mt-6 text-lg md:text-xl text-ink/70 font-light leading-relaxed max-w-2xl">
          {t.blog.lead}
        </p>
      </section>

      {/* Featured */}
      <section className="container-x pb-16">
        <Link
          to={`/blog/${featured.slug}`}
          data-testid="featured-post"
          className="group grid lg:grid-cols-12 gap-8 lg:gap-12 items-center rounded-3xl overflow-hidden bg-cream-200/40 border border-cream-300/70 p-3 md:p-3"
        >
          <div className="lg:col-span-7">
            <div className="aspect-[16/10] rounded-2xl overflow-hidden">
              <img src={featured.img} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
            </div>
          </div>
          <div className="lg:col-span-5 p-4 md:p-6 lg:pr-10">
            <div className="flex items-center gap-3 text-xs uppercase tracking-[0.22em]">
              <span className="text-terracotta-400 font-medium">{featured.category}</span>
              <span className="text-ink/40">·</span>
              <span className="text-ink/50">{featured.date}</span>
            </div>
            <h2 className="mt-4 font-serif text-3xl md:text-4xl leading-tight tracking-tight text-ink">
              {featured.title}
            </h2>
            <p className="mt-4 text-base font-light text-ink/65 leading-relaxed">{featured.excerpt}</p>
            <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-ink group-hover:text-terracotta-400 transition-colors">
              {t.blog.readArticle}
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={1.5} />
            </span>
          </div>
        </Link>
      </section>

      {/* Grid */}
      <section className="container-x pb-24 md:pb-32">
        <div className="grid md:grid-cols-2 gap-5 md:gap-6">
          {rest.map((p, i) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="group flex flex-col"
            >
              <div className="aspect-[4/3] rounded-2xl overflow-hidden bg-cream-200">
                <img src={p.img} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="mt-5">
                <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.22em]">
                  <span className="text-terracotta-400 font-medium">{p.category}</span>
                  <span className="text-ink/40">·</span>
                  <span className="text-ink/50">{p.date}</span>
                </div>
                <h3 className="mt-3 font-serif text-2xl md:text-3xl text-ink leading-tight group-hover:text-terracotta-400 transition-colors">
                  {p.title}
                </h3>
                <p className="mt-3 text-sm font-light text-ink/65 leading-relaxed">{p.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
